import DOMPurify from 'dompurify'
import { actions as notyActions } from 'layouts/ErrorBox'
import { actions as passwordActions } from '../routes/Profile/routes/ChangePassword/modules/Password'

export const isNumeric = str => {
  const parsed = Number.parseFloat(str)
  return !Number.isNaN(parsed) && Number.isFinite(parsed)
}

export const pluralize = (value, unit) => `${value} ${unit}${value === 1 ? '' : 's'}`;

export const validatePassword = str => {
  const isEightCharacters = /^.{8,}$/g
  const hasUppercase = /^(.*[A-Z].*)$/g
  const hasLowercase = /^(.*[a-z].*)$/g
  const hasNumber = /^(.*\d.*)$/g

  return (
    !!str.match(isEightCharacters) &&
    !!str.match(hasUppercase) &&
    !!str.match(hasLowercase) &&
    !!str.match(hasNumber)
  )
}

export const addErrorClass = elementIds => {
  document.querySelectorAll(`#${elementIds.join(', #')}`).forEach(element => {
    element.classList.add('has-error')
  })
}

export const PW_REQUIREMENTS = [
  '8 characters minimum',
  'One uppercase letter',
  'One lowercase letter',
  'One number'
]

// Simple email validation used by CreateUser module.
// Returns an error message string, or an empty string if the email is valid.
export const getEmailError = email => {
  if (!email) return 'Email cannot be empty'
  const trimmed = String(email).trim()
  // Basic RFC5322-ish email pattern, good enough for frontend validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(trimmed)) {
    return 'Please enter a valid email address'
  }
  return ''
}

export function getPasswordError(pw1, pw2, dispatch, strings) {
  let message
  if (pw1 !== pw2) {
    return dispatch(
      passwordActions.setPasswordError(null, 'Passwords do not match', strings)
    )
  }
  if (!validatePassword(pw1)) message = strings.linkt_pw_meet_all_criteria
  if (dispatch && message) {
    dispatch(notyActions.showError({ text: message }))
  }
  return message
}

export const _secondsToTimeString = seconds => {
  if (Number.isNaN(seconds)) return ''
  const breaks = [60, 60, 24, 7]
  const strings = ['second', 'minute', 'hour', 'day', 'week']
  let time = seconds
  let i
  for (i = 0; i < breaks.length; i++) {
    if (time >= breaks[i]) time /= breaks[i]
    else break
  }
  time = Math.floor(time)
  time = `${time} ${strings[i]}${time > 1 ? 's' : ''}`
  return time
}

export const runNTimes = (num, cb) => {
  for (let i = 0; i < num; i++) {
    cb()
  }
}

/**
 * Downloads the file on the user's browser
 * @param {blob} blob
 * @param {string} name
 */

export function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob)
  const element = document.createElement('a')
  element.setAttribute('href', url)
  element.setAttribute('target', '_blank')
  element.setAttribute('download', name)
  element.style.display = 'none'
  document.body.appendChild(element)
  element.click()
  document.body.removeChild(element)
  URL.revokeObjectURL(url)
}

export const _getNavBarOffset = () => {
  const navbar = document.getElementsByClassName('navbar')[0]
  const offset = navbar ? navbar.offsetHeight : 0
  return offset
}

// this function calculates offsets for sticky elements for use in componenetDidMount
export const calculateOffsets = (className = 'sticky') => {
  const offsetArr = []
  const navBarOffset = _getNavBarOffset() || 60
  let totalPrevOffsets = 0
  const elements = document.getElementsByClassName(className)
  const elementsArr = [...elements]
  elementsArr.forEach((el, i) => {
    if (i === 0) {
      offsetArr.push(navBarOffset)
      totalPrevOffsets += navBarOffset
    } else {
      const iterOffset = elementsArr[i - 1].offsetHeight + totalPrevOffsets
      offsetArr.push(iterOffset)
      totalPrevOffsets = iterOffset
    }
  })
  return offsetArr
}

// Remove extra newline from rich text editor output in admin panel
const removeExtraNewline = (html) => html.replace(/([^\n>])\n(?!<)/g, '$1<br/>');

export const formatInnerHTML = html => removeExtraNewline(html)

export const formatAndSanitizeHTML = html => removeExtraNewline(DOMPurify.sanitize(html))

export const replaceInString = (string, stringToReplace, newString) => {
  if (!string) return ''
  return string.replace(stringToReplace, newString)
}

export const capitalize = string => string.charAt(0).toUpperCase() + string.slice(1)

const createImage = url =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', error => reject(error))
    image.src = url
  })

const getRadianAngle = degreeValue => (degreeValue * Math.PI) / 180

/**
 * Returns the new bounding area of a rotated rectangle.
 */
const rotateSize = (width, height, rotRad) => ({
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height)
  })

const SUPPORTED_IMAGE_MIMES = ['image/png', 'image/jpeg', 'image/jpg']

/**
 * Converts unsupported image formats (webp, gif, bmp, etc.) to PNG so backend accepts them.
 * Backend only supports image/png, image/jpeg, application/pdf.
 * @param {string} base64 - Base64 image data
 * @param {string} mimeType - Original MIME type (e.g. image/webp)
 * @returns {Promise<{base64: string, mimeType: string}>}
 */
export const convertImageToSupportedFormat = async (base64, mimeType) => {
  const normalized = (mimeType || '').toLowerCase()
  if (SUPPORTED_IMAGE_MIMES.includes(normalized) || normalized === 'application/pdf') {
    return { base64, mimeType: normalized || 'image/png' }
  }
  const dataUrl = `data:${normalized || 'image/png'};base64,${base64}`
  const img = await createImage(dataUrl)
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) return { base64, mimeType: 'image/png' }
  ctx.drawImage(img, 0, 0)
  const pngDataUrl = canvas.toDataURL('image/png')
  const pngBase64 = pngDataUrl.split(',')[1]
  return { base64: pngBase64, mimeType: 'image/png' }
}

export const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  const { x, y, width, height } = pixelCrop
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) return null

  const rotRad = getRadianAngle(rotation)

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotRad
  )

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth
  canvas.height = bBoxHeight

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2)
  ctx.rotate(rotRad)
  ctx.translate(-image.width / 2, -image.height / 2)

  // draw image
  ctx.drawImage(image, 0, 0)

  // croppedAreaPixels values are bounding box relative
  // extract the cropped image using these values
  const data = ctx.getImageData(x, y, width, height)

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = width
  canvas.height = height

  // paste generated rotate image at the top left corner
  ctx.putImageData(data, 0, 0)

  // As Base64 string
  const base64 = canvas.toDataURL('image/jpeg')
  return new Promise(resolve => {
    canvas.toBlob(blob => {
      resolve({
        blob,
        base64,
        file: new File([blob], `${new Date().getTime()}.jpeg`, {
          type: 'image/jpeg'
        })
      })
    }, 'image/jpeg')
  })
}
